// src/services/address/providers/viacep/adapter.js
// *******************************************************************************
// ViaCEP Provider Adapter
// API Documentation: https://viacep.com.br/
// *******************************************************************************

import fetchX from 'utils/fetchX';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';

const REQUEST_TIMEOUT = 10000; // 10 seconds

/**
 * Fetches address data from ViaCEP API
 * @param {Object} options - Request options
 * @param {string} options.cep - CEP number (8 digits, no mask)
 * @returns {Promise<Object>} Raw response from ViaCEP
 * @throws {Error} When API fails (timeout, connection error, etc)
 */
export const searchAddress = async options => {
  const { cep } = options;

  if (!cep || !/^\d{8}$/.test(cep)) {
    throw new Error('CEP inválido. Deve conter 8 dígitos.');
  }

  const url = mergeUrlParamsQuery({
    url: API.PROVIDERS.CEP.VIACEP,
    params: { cep },
  });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort(new Error('Request timeout'));
  }, REQUEST_TIMEOUT);

  try {
    const response = await fetchX(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // ViaCEP returns {erro: true} when CEP not found (but API is working)
    if (data?.erro === true || data?.erro === 'true') {
      return {
        provider: 'viacep',
        success: false,
        apiWorked: true, // API worked, but CEP doesn't exist
        error: 'CEP não encontrado',
      };
    }

    return {
      provider: 'viacep',
      success: true,
      ...data,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    return {
      provider: 'viacep',
      success: false,
      error: error.name === 'AbortError' ? 'Timeout' : error.message,
    };
  }
};

/**
 * Normalizes request payload to adapter format
 * @param {Object} base - Base request object
 * @param {string} base.cep - CEP number
 * @returns {Object} Normalized request
 */
export const normalizeRequestPayload = base => {
  let cep = base?.cep ?? null;

  if (typeof cep === 'string') {
    // Remove non-numeric characters
    cep = cep.replace(/\D/g, '');
  }

  if (!cep || !/^\d{8}$/.test(cep)) {
    throw new Error('CEP inválido');
  }

  return { cep };
};

/**
 * Normalizes response to universal POJO format
 * @param {Object} res - Raw response from ViaCEP
 * @returns {Object} Universal POJO
 *
 * ViaCEP Response format:
 * {
 *   cep: "13010-111",
 *   logradouro: "Rua General Osório",
 *   complemento: "de 762/763 a 1661/1662",
 *   unidade: "",
 *   bairro: "Centro",
 *   localidade: "Campinas",
 *   uf: "SP",
 *   estado: "São Paulo",
 *   regiao: "Sudeste",
 *   ibge: "3509502",
 *   gia: "2446",
 *   ddd: "19",
 *   siafi: "6291"
 * }
 */
export const normalizeResponseBody = res => {
  if (!res || typeof res !== 'object') {
    return {
      provider: 'viacep',
      success: false,
      error: 'Invalid response',
    };
  }

  // If error in response
  if (res.success === false || res.error) {
    return {
      provider: 'viacep',
      success: false,
      apiWorked: res.apiWorked || false, // Preserve apiWorked flag
      error: res.error || 'Unknown error',
    };
  }

  // Detect if CEP is municipality-wide (multiple addresses)
  // When logradouro is empty, it means the CEP covers the entire city
  const hasMultipleAddresses = !res.logradouro || res.logradouro.trim() === '';

  return {
    // Identification
    zip: res.cep?.replace(/\D/g, '') || '',
    source: 'viacep',
    success: true,

    // Address fields (universal POJO keys)
    street: res.logradouro || '',
    unit: res.unidade || '',
    neighborhood: res.bairro || '',
    city: res.localidade || '',
    state: res.uf || '',
    stateName: res.estado || '',
    region: res.regiao || '',

    // Additional codes
    areaCode: res.ddd || '',
    ibgeCode: res.ibge || '',
    giaCode: res.gia || '',
    siafiCode: res.siafi || '',

    // Metadata
    hasMultipleAddresses,
    error: null,
  };
};

export default {
  searchAddress,
  normalizeRequestPayload,
  normalizeResponseBody,
};
